import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";

// Define a more compatible interface for the canvas
interface VisEditorCanvas extends HTMLCanvasElement {
  renderWithOverlay?: () => Promise<(() => void) | undefined>;
  isExporting?: boolean;
}

// Export a function to capture the proof image and return the data URL
export async function captureProofImage(): Promise<string | null> {
  // Find the main canvas from the VisEditor
  const mainCanvas = document.querySelector(
    ".vis-editor-main"
  ) as HTMLCanvasElement;

  if (!mainCanvas) {
    console.error("captureProofImage: Main canvas not found");
    return null;
  }

  // Cast to our interface type after getting the element
  const visCanvas = mainCanvas as unknown as VisEditorCanvas;

  try {
    // Set the exporting flag to true (this will be reset by cleanup)
    visCanvas.isExporting = true;

    // Force a cleanup of any pending animations to ensure a clean state
    window.dispatchEvent(new Event("resize"));

    // Wait a small amount of time to ensure the canvas has fully rendered with the flag set
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Get the main canvas context
    const mainCtx = mainCanvas.getContext("2d");
    if (!mainCtx) {
      console.error("captureProofImage: Failed to get main canvas context");
      visCanvas.isExporting = false;
      return null;
    }

    // Create a temporary canvas to capture the design
    const tempCanvas = document.createElement("canvas");
    const tempCtx = tempCanvas.getContext("2d", { alpha: true });

    if (!tempCtx) {
      console.error(
        "captureProofImage: Failed to get temporary canvas context"
      );
      visCanvas.isExporting = false;
      return null;
    }

    // Set the temporary canvas to match the main canvas dimensions
    tempCanvas.width = mainCanvas.width;
    tempCanvas.height = mainCanvas.height;

    // Draw the main canvas onto the temporary canvas exactly as is
    // This preserves the exact visual state including positioning
    tempCtx.drawImage(
      mainCanvas,
      0,
      0,
      mainCanvas.width,
      mainCanvas.height,
      0,
      0,
      tempCanvas.width,
      tempCanvas.height
    );

    // Convert the temporary canvas to a data URL with high quality
    const dataUrl = tempCanvas.toDataURL("image/png", 1.0);

    // Reset the exporting flag
    visCanvas.isExporting = false;

    // Force a re-render to restore the editor view
    window.dispatchEvent(new Event("resize"));

    return dataUrl;
  } catch (error) {
    console.error("captureProofImage: Error capturing proof image:", error);
    // Make sure to reset the exporting flag in case of error
    visCanvas.isExporting = false;
    return null;
  }
}

const DownloadProof = ({
  partNumber = "part",
  decalNumber = "decal",
}: {
  partNumber?: string;
  decalNumber?: string;
}) => {
  const handleDownload = async () => {
    const dataUrl = await captureProofImage();

    if (!dataUrl) {
      return;
    }

    // Create a download link and trigger it
    const link = document.createElement("a");
    link.download = `${partNumber}-${decalNumber}.png`;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Button onClick={handleDownload} variant='outline'>
      <Download className='w-4 h-4 mr-2' />
      Download Proof
    </Button>
  );
};

export default DownloadProof;
